import React, { useCallback, useEffect } from 'react';

import Home, { AccountRouter } from '@bessemercws/accounts';
import { useAuthService } from '@bessemercws/auth';
import { NotFound, PendingUserType, UnknownUserType } from '@bessemercws/components';
import DocumentRouter from '@bessemercws/documents';
import { isUserProfileType } from '@bessemercws/graphql';
import InsightRouter from '@bessemercws/insights';
import { AdvisorHome, LoginProvider } from '@bessemercws/login';
import { App as PreferencesRouter } from '@bessemercws/preferences';
import { useClientContext } from '@bessemercws/providers';
import { normalizePath } from '@bessemercws/utils';
import { encode } from 'base-64';
import { Route, useLocation, useNavigate } from 'react-router-dom';

import Quicken from '#/components/Quicken';
import { RequireAuth } from '#/components/RequireAuth/RequireAuth';
import { useUserProfileSetup } from '#/hooks/user/useUserProfileSetup';
import { CustomBody, WithHeader } from '#/layouts/index';
import { emulationRoutesByUserProfileType } from './UserProfileTypeRoutesConfig';

function getHomeRoute(basePath: string, selectedClient: string, userProfileType?: string | undefined | null ): string {
    const encodedClient = encode(String(selectedClient));
    if (userProfileType && isUserProfileType(userProfileType) && emulationRoutesByUserProfileType[userProfileType]) {
      return `${basePath}/${encodedClient}${emulationRoutesByUserProfileType[userProfileType].homeRoute}`;
    } else {
      return `${basePath}/${encodedClient}`;
    }
}

export const EmulatorRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdvisor, setSelectedClient, selectedClient, trackingId, setUserId, idToken, subscriptionChannel } =
    useClientContext();
  const { getAuthorizationWithRedirectURL } = useAuthService();
  const redirectURL = getAuthorizationWithRedirectURL();
  const advisorsBasePath = 'advisors';

  const { isLoading, hasUserIdHash, userProfileType, advisorData, advisorError } = useUserProfileSetup({
    isAdvisor: true,
    selectedClient,
  });

  useEffect(() => {
    if (!advisorData) return;
    if (normalizePath(location.pathname) === `/${advisorsBasePath}/${encode(String(selectedClient))}`) {
      navigate(getHomeRoute(advisorsBasePath, selectedClient!, userProfileType));
    }
  }, [advisorData, location.pathname, navigate, selectedClient, userProfileType]);

  useEffect(() => {
    if (!advisorError) return;
    navigate(getHomeRoute(advisorsBasePath, selectedClient!));
  }, [advisorError, navigate, selectedClient]);

  const handleNavigateHome = useCallback(() => {
    navigate(getHomeRoute(advisorsBasePath, selectedClient!, userProfileType));
  }, [userProfileType, navigate]);

  function handleClientEmulationBegin(clientId: string) {
    setSelectedClient(clientId);
  }

  const handleSetUsername = (username: string) => {
    setUserId(username);
  };

  let userProfileTypeWithHeaderRouteElements: React.JSX.Element[], userProfileTypeCustomBodyRouteElements: React.JSX.Element[];
  if (userProfileType && isUserProfileType(userProfileType) && emulationRoutesByUserProfileType[userProfileType]) {
    const { withHeaderRoutes, customBodyRoutes } = emulationRoutesByUserProfileType[userProfileType]
      .routes({ 
        token: idToken,
        clientId: selectedClient,
        subscriptionChannel,
        userProfileType,
        isAdvisor,
        onNavigateHome: handleNavigateHome
      });
    userProfileTypeWithHeaderRouteElements = withHeaderRoutes
      .map(({ path, element }, index) => <Route key={index} path={path} element={element} />);
    userProfileTypeCustomBodyRouteElements = customBodyRoutes
      .map(({ path, element }, index) => <Route key={index} path={path} element={element} />);
  }

  return (
    <Route path={advisorsBasePath}>
      <Route
        index
        element={
          <LoginProvider
            trackingId={trackingId}
            onClientEmulationBegin={handleClientEmulationBegin}
            setUsername={handleSetUsername}
          >
            <RequireAuth redirectTo={redirectURL}>
              <AdvisorHome />
            </RequireAuth>
          </LoginProvider>
        }
      />
      { renderRoutes() }
    </Route>
  );

  function renderRoutes(): React.JSX.Element {
    if (isLoading || (!advisorData && !advisorError)) {
      return (
        <Route
          path=":clientId/*"
          element={
            <RequireAuth redirectTo={redirectURL}>
              <WithHeader />
            </RequireAuth>
          }
        ></Route>
      );
    } else if (!userProfileType) {
      return renderRoutesWithoutUserProfileType();
    } else if (userProfileType && userProfileTypeWithHeaderRouteElements && userProfileTypeCustomBodyRouteElements) {
      return renderRoutesWithUserProfileType();
    }
    return renderDefaultRoutes();
  }

  function renderRoutesWithoutUserProfileType() {
    return (
      <Route
        path=":clientId/*"
        element={
          <RequireAuth redirectTo={redirectURL}>
            <WithHeader />
          </RequireAuth>
        }
      >
        <Route index element={hasUserIdHash ? <UnknownUserType /> : <PendingUserType />} />
        <Route path="preferences/*" element={<PreferencesRouter isAdvisor={isAdvisor} />} />
        <Route path="*" element={<NotFound onNavigateHome={handleNavigateHome} />} />
      </Route>
    )
  }

  function renderRoutesWithUserProfileType() {
    return (
      <>
        <Route
          path=":clientId"
          element={
            <RequireAuth redirectTo={redirectURL}>
              <WithHeader />
            </RequireAuth>
          }
        >
          { userProfileTypeWithHeaderRouteElements }
          <Route path="*" element={<NotFound onNavigateHome={handleNavigateHome} />} />
        </Route>
        <Route
          path=":clientId"
          element={
            <RequireAuth redirectTo={redirectURL}>
              <CustomBody />
            </RequireAuth>
          }
        >
          { userProfileTypeCustomBodyRouteElements }
        </Route>
      </>
    )
  }

  function renderDefaultRoutes() {
    return (
      <>
        <Route
          path=":clientId"
          element={
            <RequireAuth redirectTo={redirectURL}>
              <WithHeader />
            </RequireAuth>
          }
        >
          <Route
            index
            element={<Home subscriptionChannel={subscriptionChannel} isAdvisor={isAdvisor} clientId={selectedClient} />}
          />
          <Route path="preferences/*" element={<PreferencesRouter isAdvisor={isAdvisor} />} />
          <Route path="insights/*" element={<InsightRouter />} />
          <Route path="quicken/*" element={<Quicken />} />
          <Route path="*" element={<NotFound onNavigateHome={handleNavigateHome} />} />
        </Route>
        <Route
          path=":clientId"
          element={
            <RequireAuth redirectTo={redirectURL}>
              <CustomBody />
            </RequireAuth>
          }
        >
          <Route
            path="account/*"
            element={
              <AccountRouter isAdvisor={isAdvisor} clientId={selectedClient} subscriptionChannel={subscriptionChannel} />
            }
          />
          <Route
            path="documents/*"
            element={
              <DocumentRouter
                isAdvisor={isAdvisor}
                clientId={selectedClient}
                subscriptionChannel={subscriptionChannel}
                token={idToken}
                onNavigateHome={handleNavigateHome}
              />
            }
          />
        </Route>
      </>
    )
  }
};

export default EmulatorRoutes;
